/**
 * What a refused spreadsheet says (board T2, its size copy from T3): why the file is not read, and
 * how to fix it. Every refusal's words are here, in one place, so a sentence the owner has not
 * written yet is swapped in one line.
 *
 * A file the listing refuses takes a card in the zone. A refusal of the CHOSEN sheets
 * (`sheetTooLarge`, `sheetsInvalid`) is said in the picker, which stays, with what to change there.
 * The refusals with no owner copy yet (`unreadable`, `notKept`, owner OK 5 October 2026) share one
 * neutral card in the same style until it lands.
 */

import type { TFunction } from 'i18next';

import type { AiImportRefusalReason } from '~/entities/ai-conversation';
import { numberFormat } from '~/shared/lib/intlCache';

import { IMPORT_LIMITS } from '../config/importLimits';

const KEY = 'web:aiPlan.import';

/** The refusals the owner has worded; the others take the generic card. */
const WORDED = ['xls', 'link', 'macros', 'tooLarge', 'tooManySheets', 'unpackedTooLarge'] as const;

type Worded = (typeof WORDED)[number];

function isWorded(reason: AiImportRefusalReason): reason is Worded {
  return (WORDED as readonly string[]).includes(reason);
}

/** „2 MB" for a byte count, the way the trainer reads a file size. */
export function megabytes(locale: string, bytes: number): string {
  const value = numberFormat(locale, { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024));
  return `${value} MB`;
}

export interface RefusalWords {
  /** The bold first sentence: the file is not read, and why. */
  readonly title: string;
  /** How to fix it. */
  readonly fix: string;
}

/**
 * The refusal's two sentences. `limit` is the bound the server named, in bytes for the file's size;
 * without one (the transport's own bound answers without it) the configured size stands.
 */
export function refusalWords(t: TFunction, locale: string, reason: AiImportRefusalReason, limit: number | null): RefusalWords {
  if (!isWorded(reason)) return { title: t(`${KEY}.refused.generic.title`), fix: t(`${KEY}.refused.generic.fix`) };
  const size = reason === 'tooLarge' ? megabytes(locale, limit ?? IMPORT_LIMITS.maxFileBytes) : null;
  return {
    title: t(`${KEY}.refused.${reason}.title`, size === null ? {} : { size }),
    fix: t(`${KEY}.refused.${reason}.fix`),
  };
}

/** „.xlsx albo .csv, do 2 MB i 10 arkuszy". */
export function acceptedFileWords(t: TFunction, locale: string): string {
  return t(`${KEY}.drop.accepted`, { size: megabytes(locale, IMPORT_LIMITS.maxFileBytes), sheets: IMPORT_LIMITS.maxSheets });
}

/**
 * Refusals of the sheets chosen, said inside the picker: the trainer changes the choice there. A
 * missing scheduling mode is answered by the picker's own question rather than in words.
 */
export function isChoiceRefusal(reason: AiImportRefusalReason): boolean {
  return reason === 'sheetTooLarge' || reason === 'sheetsInvalid' || reason === 'modeRequired';
}

/** What to do about a refused choice: leave out the named sheet, or fewer sheets together. */
export function choiceRefusalWords(t: TFunction, reason: AiImportRefusalReason, sheet: string | null): string {
  if (reason === 'sheetsInvalid') return t(`${KEY}.picker.refused.sheetsInvalid`);
  return sheet === null ? t(`${KEY}.picker.refused.tooMuchTogether`) : t(`${KEY}.picker.refused.sheetTooLarge`, { sheet });
}
